import { Box, Button, CircularProgress, Collapse } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'commons/store/hooks';
import { selectCurrentSection, selectEcrf } from 'features/ecrf/redux/ecrfSlice';
import React, { useState } from 'react';
import {
    StyledEcrfPanel,
    StyledEcrfTitle,
    StyledEcrfSubTitle,
    StyledEcrfSectionsTitle,
    StyledEcrfSectionsTitleWrapper,
    StyledEcrfSectionsWrapper,
    StyledEcrfTitleFull,
    StyledButtonShow,
    StyledEcrfTitleSmall,
    StyledEcrfTitleRand,
    StyledEcrfSubTitleSmall,
} from '../Ecrf.styled';
import { MenuItem } from './MenuItem';
import DateTime from "core/helpers/date/DateTime";
import { COLOR_SECONDARY_DARK, DATETIME_FORMAT } from "commons/constants/env";
import { roles } from 'commons/auth/roles';
import { selectUserProfile } from "core/redux/userSlice";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { exportEcrfData, selectEcrfResourceLoading } from "../../../redux/ecrfDataManagementSlice";
import Role from "core/helpers/Role";

interface Props {
    selectedVisit: any;
    selectedSection: any;
    setSelectedSection: (selectedSection: any) => void;
    isDataLoading: boolean;
    setIsDataLoading: (isDataLoading: boolean) => void;
}

export const ecrfStatusDictionary = [
    'active',
    'suspended',
    'excluded',
    'randomized',
    'signed',
    'completed',
    'screen-failure',
    'blocked'
];

export const LeftPanel: React.FC<Props> = ({
   selectedVisit,
   selectedSection,
   isDataLoading,
   setIsDataLoading,
   setSelectedSection
}) => {
    const dispatch = useAppDispatch();
    /** This is current selected section data */
    const currentSection = useAppSelector(selectCurrentSection);

    /** Current ecrf all data */
    const ecrf = useAppSelector(selectEcrf);
    const isEcrfResourceLoading = useAppSelector(selectEcrfResourceLoading);

    /** Current user profile */
    const userProfile = useAppSelector(selectUserProfile);

    const [expandDetails, setExpandDetails] = useState<boolean>(false);
    const [expandSections, setExpandSections] = useState<boolean>(true);
    const [showPanel, setShowPanel] = useState(false)
    const { t } = useTranslation();

    let displayCount: boolean = userProfile.roles.some(
      (role: string) => roles.admin.includes(role) || roles.control.includes(role)
    );

    let hasOnlyUserRole: boolean = Role.hasOnlyDefaultRole(userProfile.roles);

    return (
        <StyledEcrfPanel show={showPanel}>
            <StyledButtonShow>
                <Button
                  onClick={() => setShowPanel(!showPanel)}
                  variant={'outlined'}
                  style={{ position: 'fixed', bottom: '60px', right: '10px' }}
                >
                  {showPanel ? t('hide') : t('show')}
                </Button>
            </StyledButtonShow>

            <Box>
              <Box>
                <StyledEcrfTitle>{t('medical-center')}</StyledEcrfTitle>
                <StyledEcrfSubTitle>{ecrf?.medicalCenter?.name || '-'}</StyledEcrfSubTitle>
              </Box>
              {!hasOnlyUserRole && <Box mt="15px">
                <StyledEcrfTitle><b>{t('screening-number')}</b> / {t('randomization')}</StyledEcrfTitle>
                <StyledEcrfSubTitle>
                  {ecrf?.screeningNumber || '-'} / <StyledEcrfTitleRand>{ecrf?.randomId || '-'}</StyledEcrfTitleRand>
                </StyledEcrfSubTitle>
              </Box>}
            </Box>

            <Box mt={3}>
              <StyledEcrfSectionsTitleWrapper onClick={() => setExpandDetails(!expandDetails)}>
                <StyledEcrfSectionsTitle>{t('details')}</StyledEcrfSectionsTitle>
                {expandDetails ? <ExpandLess /> : <ExpandMore />}
              </StyledEcrfSectionsTitleWrapper>

              <Collapse in={expandDetails} unmountOnExit>
                <Box mt="15px">
                  <StyledEcrfTitle>{t('randomization-date')}</StyledEcrfTitle>
                  <StyledEcrfSubTitle>
                    {DateTime.toIso(ecrf?.randomAt, DATETIME_FORMAT) || '-'}
                  </StyledEcrfSubTitle>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                  <Box mr={'auto'}>
                    <StyledEcrfTitleSmall>{t('created-date')}</StyledEcrfTitleSmall>
                    <StyledEcrfSubTitleSmall>
                      {DateTime.toIso(ecrf?.createdAt, DATETIME_FORMAT) || '-'}
                    </StyledEcrfSubTitleSmall>
                  </Box>
                  {(ecrf?.randomDrug && !isEmpty(ecrf?.randomDrug)) && (
                    <Box mr={'auto'}>
                      <StyledEcrfTitleSmall>{t('arm')}</StyledEcrfTitleSmall>
                      <StyledEcrfSubTitleSmall>{ecrf?.randomDrug || ''}</StyledEcrfSubTitleSmall>
                    </Box>
                  )}
                </Box>

                <Box mt="15px" pt={'15px'} style={{borderTop: `1px solid ${COLOR_SECONDARY_DARK}`}}>
                    <StyledEcrfTitle>{t('visible')}: {currentSection?.name}</StyledEcrfTitle>
                </Box>

                <Box mt="15px">
                    <StyledEcrfTitle>{t('status')}: {t(ecrfStatusDictionary[ecrf?.status])}</StyledEcrfTitle>
                </Box>

                {!hasOnlyUserRole && <Box mt="15px" pt={'15px'} style={{borderTop: `1px solid ${COLOR_SECONDARY_DARK}`}}>
                  <StyledEcrfTitleFull onClick={() => dispatch(exportEcrfData(ecrf?.id))}>
                    {isEcrfResourceLoading
                      ? <CircularProgress color="inherit" size={12} style={{marginRight: '5px'}}/>
                      : undefined}
                    {t('export-to')} .xlsx
                  </StyledEcrfTitleFull>
                </Box>}
              </Collapse>
            </Box>

            <Box mt={3} mb={5}>
                {selectedVisit?.sections && (
                    <>
                        <StyledEcrfSectionsTitleWrapper onClick={() => setExpandSections(!expandSections)}>
                            <StyledEcrfSectionsTitle>{t('sections')}</StyledEcrfSectionsTitle>
                            {isDataLoading ? (
                                <CircularProgress color="inherit" size={12} />
                            ) : (
                                <> {expandSections ? <ExpandLess /> : <ExpandMore />} </>
                            )}
                        </StyledEcrfSectionsTitleWrapper>
                    </>
                )}

                <Box mt="20px">
                    <Collapse in={expandSections} unmountOnExit>
                        <StyledEcrfSectionsWrapper>
                            {selectedVisit?.sections && (
                                <Box>
                                    {selectedVisit?.sections.map((section: any) => (
                                        <MenuItem
                                            key={section?.id}
                                            section={section}
                                            selectedSection={selectedSection}
                                            setSelectedSection={setSelectedSection}
                                            isDataLoading={isDataLoading}
                                            setIsDataLoading={setIsDataLoading}
                                            displayCount={displayCount}
                                        />
                                    ))}
                                </Box>
                            )}
                        </StyledEcrfSectionsWrapper>
                    </Collapse>
                </Box>
            </Box>
        </StyledEcrfPanel>
    );
};
